import { supabase } from "@/lib/supabase";

/**
 * Call an HQ Netlify function as the signed-in employee.
 *
 * BE-SEC-007 and its neighbours. A long list of functions read with the service
 * key — which bypasses RLS — and asked nobody who was calling, because the
 * pages calling them never sent the token they already had. Both halves are the
 * same omission seen from two ends.
 *
 * Using this instead of bare fetch means the header is not something each call
 * site has to remember. The functions reject a request without it, so a missed
 * one now fails loudly in development rather than quietly serving company data
 * to anyone who finds the URL.
 */
export async function fnFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(path.startsWith("/") ? path : `/.netlify/functions/${path}`, { ...init, headers });
}

/** fnFetch plus the JSON parse both sides of every call site were doing. */
export async function fnJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fnFetch(path, init);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const message = (body as { message?: string; error?: string }).message
      ?? (body as { error?: string }).error
      ?? `Request failed (${r.status})`;
    throw new Error(message);
  }
  return body as T;
}
